import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, ArrowRight, Save, Upload, Plus, Trash2, FileText, Info, Eye, Download, Loader2
} from 'lucide-react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import axios from 'axios';
import './BeelineSubmissionForm.css'; 

// --- UNIVERSAL DATE FORMATTER (Forces MM-DD-YYYY for PDFs and Read-Only Views) ---
const formatToMMDDYYYY = (dateStr) => {
  if (!dateStr || dateStr === 'NA' || dateStr === 'N/A') return dateStr || '';
  
  // If it's already MM-DD-YYYY, return it
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr.replace(/\//g, '-');
  
  // If it's YYYY-MM-DD from an HTML date picker or DB, flip it
  const parts = dateStr.split('T')[0].split('-'); 
  if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[1]}-${parts[2]}-${parts[0]}`; // Returns MM-DD-YYYY
  }
  
  // Fallback for native Date objects
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
  }
  return dateStr;
};

const sanitizeForPDF = (text) => {
  if (text === null || text === undefined) return '';
  return text
    .toString()
    .normalize('NFKC')
    .replace(/[\u2010-\u2015\u2212\u00AD\uFE58\uFE63\uFF0D]/g, '-')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u00A0\u2007\u202F\u2009\u200A\u2002\u2003]/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\u2026/g, '...')
    .replace(/\t/g, ' ')
    .replace(/[^\x20-\x7E\xA0-\xFF\n]/g, '?');
};

export default function BeelineSubmissionForm({ job, partner, onBack, initialData, prefillData }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [resumeFile, setResumeFile] = useState(null); 
  const [resumeUrl, setResumeUrl] = useState('');    

  // --- PRE-FILL DATA FOR EDIT MODE ---
  useEffect(() => {
    if (initialData) {
      console.log("Edit Mode Active. Loading candidate data...");
      setFormData(prev => ({
        ...prev,
        ...initialData,
        // Format dates safely for HTML <input type="date"> (Must be YYYY-MM-DD)
        dob: initialData.dob ? initialData.dob.split('T')[0] : '',
        dateAvailable: initialData.dateAvailable ? initialData.dateAvailable.split('T')[0] : '',
        educationStartDate: initialData.educationStartDate ? initialData.educationStartDate.split('T')[0] : '',
        educationEndDate: initialData.educationEndDate ? initialData.educationEndDate.split('T')[0] : '',
        currentOrgStartDate: initialData.currentOrgStartDate ? initialData.currentOrgStartDate.split('T')[0] : '',
        currentOrgEndDate: initialData.currentOrgEndDate ? initialData.currentOrgEndDate.split('T')[0] : '',
        panNumber: initialData.panNumber || '',
        interviewDate: initialData.interviewDate ? initialData.interviewDate.split('T')[0] : '',
        visaExpiry: initialData.visaExpiry ? initialData.visaExpiry.split('T')[0] : '',
        // Ensure arrays load safely
        interviewSlots: initialData.interviewSlots?.length 
            ? initialData.interviewSlots.map(slot => ({...slot, date: slot.date ? slot.date.split('T')[0] : ''}))
            : [{ date: '', timeSlot: '' }],
        skillMatrix: initialData.skillMatrix?.length 
            ? initialData.skillMatrix 
            : [{ skill: '', years: '' }],
      }));
    }
  }, [initialData]);

  useEffect(() => {
    if (!initialData) console.log("Beeline Form Loaded (New Submission).");
  }, [job, partner, initialData]);

  // --- MAIN FORM STATE ---
  const [formData, setFormData] = useState({
    // --- 1. IDENTITY & BASIC INFO ---
    requestId: job?.requestId || '',
    beelineId: job?.beelineId || '',
    firstName: '',
    lastName: '',
    email: '',
    dob: '',
    initialPersonalId: '',
    fullPersonalId: '', 
    panNumber: '', 
    
    // TCS Status
    tcsFormerEmployee: 'No',
    tcsFormerBusinessAssociate: 'No',
    formerTcsEmployeeId: 'NA', 

    gender: '',
    nationality: 'Indian',
    availability: 'Yes',
    dateAvailable: '',
    linkedInLink: '',
    mobile: '',

    // --- 2. ADDITIONAL INFO ---
    totalITExperience: '',
    experienceRequiredSkills: '',
    highestDegree: '',
    specialization: '',
    educationStartDate: '',
    educationEndDate: '',
    recentCompany: '',
    recentCompanyAddress: '',
    currentOrgStartDate: '',
    currentOrgEndDate: '',
    candidateAddress: '',
    candidateCity: '',
    candidateState: '',
    candidatePincode: '',

    // --- 3. CANDIDATE INFO ---
    resourceTypeId: 'Contingent Worker',
    submittedRate: '',
    submittedPayRate: '',

    // --- STEP 2 HELPERS ---
    interviewSlots: [{ date: '', timeSlot: '' }],
    skillMatrix: [{ skill: '', years: '' }],
    supplierComments: '', 

    // --- STEP 3: ATTESTATION (HARDCODED) ---
    supplierName: 'TENEO ADVISOROPEDIA PRIVATE LIMITED',
    employedBy: 'TENEO ADVISOROPEDIA PRIVATE LIMITED',
    
    resumeVerified: 'Yes',
    formalInterviewConducted: 'Yes',
    interviewDate: '',
    workAuthType: '',
    
    visaSponsorship: 'No',
    visaType: 'NA',
    visaExpiry: 'NA',
    visaSponsorTransfer: 'No',
    
    // Signature Block (HARDCODED)
    signerName: 'Shweta Srivastava',
    signerTitle: 'Account Manager',
    signerEmail: 'Shweta.Srivastava@talentoza.com',
    digitalSignature: 'Shweta Srivastava', 
    signerDate: formatToMMDDYYYY(new Date().toISOString()), 
    submissionDate: formatToMMDDYYYY(new Date().toISOString())
  });

    // --- PREFILL FROM EXISTING CANDIDATE (new submission, not edit) ---
  useEffect(() => {
    if (!prefillData) return;
    const { resumeUrl: storedUrl, ...rest } = prefillData;
    const clean = Object.fromEntries(
      Object.entries(rest).filter(([, v]) => v !== '' && v != null && !(Array.isArray(v) && v.length === 0))
    );
    setFormData(prev => ({ ...prev, ...clean }));

    if (storedUrl) {
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/candidates/download-file`, {
        params: { url: storedUrl }, responseType: 'blob'
      }).then(r => {
        if (!r.data.type.includes('pdf')) {
          alert('Saved resume is not a PDF. Please upload a PDF resume in this form.');
          return;
        }
        const f = new File([r.data], `${rest.firstName || 'candidate'}_resume.pdf`, { type: 'application/pdf' });
        setResumeFile(f);
        setResumeUrl(URL.createObjectURL(f));
      }).catch(() => alert('Could not load the saved resume. Please upload it manually.'));
    }
  }, [prefillData]);

  // --- AUTO-GENERATE IDs ---
  useEffect(() => {
    const { dob, firstName, lastName } = formData;
    if (dob) {
        const parts = dob.split('-');
        if (parts.length === 3) {
            const month = parts[1]; 
            const day = parts[2];   
            
            // 4 chars reserved for date
            const dateSuffix = `${month}${day}`;
            const generatedInitialId = dateSuffix;
            
            let generatedFullId = '';
            
            if (firstName && lastName) {
                // 1. Combine Name: UPPERCASE, Remove Spaces
                const fNameClean = firstName.trim().toUpperCase().replace(/\s+/g, '');
                const lNameClean = lastName.trim().toUpperCase().replace(/\s+/g, '');
                
                let combinedName = fNameClean + lNameClean;

                // 2. Truncate Name to Max 22 Characters
                // (Total 26 chars allowed - 4 chars for date = 22 chars for name)
                if (combinedName.length > 22) {
                    combinedName = combinedName.substring(0, 22);
                }

                // 3. Combine Name + Date
                generatedFullId = `${combinedName}${dateSuffix}`;
            }

            setFormData(prev => {
                if (prev.initialPersonalId !== generatedInitialId || prev.fullPersonalId !== generatedFullId) {
                    return { ...prev, initialPersonalId: generatedInitialId, fullPersonalId: generatedFullId };
                }
                return prev;
            });
        }
    }
  }, [formData.dob, formData.firstName, formData.lastName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

    const handlePanChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, panNumber: value }));
  };

  const handleSubmittedRateChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      submittedRate: value,
      // Field 34 = Field 33 + 32%  (rounded to 2 decimals)
      submittedPayRate: value === '' || isNaN(parseFloat(value))
        ? ''
        : String(Math.round(parseFloat(value) * 1.32))
    }));
  };

  const handleWholeNumberChange = (e) => {
    const { name, value } = e.target;
    const digitsOnly = value.replace(/\D/g, ''); // removes everything except 0-9
    setFormData(prev => ({ ...prev, [name]: digitsOnly }));
  };

  const handleTcsStatusChange = (e) => {
      const value = e.target.value;
      setFormData(prev => ({
          ...prev,
          tcsFormerEmployee: value,
          formerTcsEmployeeId: value === 'No' ? 'NA' : (prev.formerTcsEmployeeId === 'NA' ? '' : prev.formerTcsEmployeeId)
      }));
  };

  // --- FILE UPLOAD ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // --- NEW: STRICT PDF VALIDATION ---
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        alert("Invalid file type! Please upload a PDF file only.");
        e.target.value = null; // Clears the wrong file from the input
        return;
    }

    setIsUploading(true);
    const uploadData = new FormData();
    uploadData.append('resume', file);
    try {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/beeline/parse`, uploadData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        const parsed = response.data.data;
        setFormData(prev => ({
            ...prev,
            firstName: parsed.firstName || prev.firstName,
            lastName: parsed.lastName || prev.lastName,
            email: parsed.email || prev.email,
            mobile: parsed.mobile || prev.mobile,
            linkedInLink: parsed.linkedInLink || prev.linkedInLink,
                        totalITExperience: parsed.totalITExperience
              ? String(parseInt(parsed.totalITExperience, 10) || '')
              : prev.totalITExperience,
            highestDegree: parsed.highestDegree || prev.highestDegree,
            specialization: parsed.specialization || prev.specialization,
            recentCompany: parsed.recentCompany || prev.recentCompany,
            candidateCity: parsed.candidateCity || prev.candidateCity,
            candidateState: parsed.candidateState || prev.candidateState,
            tcsFormerEmployee: parsed.tcsFormerEmployee || prev.tcsFormerEmployee,
        }));
        setResumeFile(file);
        setResumeUrl(URL.createObjectURL(file)); 
        alert("Resume parsed!");
    } catch (error) {
        console.error("Parsing Error:", error);
        setResumeFile(file);
        setResumeUrl(URL.createObjectURL(file)); 
    } finally {
        setIsUploading(false);
    }
  };

  const handleAddSlot = () => setFormData(prev => ({ ...prev, interviewSlots: [...prev.interviewSlots, { date: '', timeSlot: '' }] }));
  const handleSlotChange = (index, field, value) => {
    const updated = [...formData.interviewSlots];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, interviewSlots: updated }));
  };
  const handleAddSkill = () => setFormData(prev => ({ ...prev, skillMatrix: [...prev.skillMatrix, { skill: '', years: '' }] }));
  const handleSkillChange = (index, field, value) => {
    const updated = [...formData.skillMatrix];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, skillMatrix: updated }));
  };

  // ============================================================
  // PDF GENERATION (PIXEL-PERFECT PONTOON MATCH)
  // ============================================================

  const generateCoverPagePDF = async () => {
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([595.28, 841.89]);
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const drawText = (text, x, y, size = 10, isBold = false) => {
        page.drawText(sanitizeForPDF(text), { x, y, size, font: isBold ? boldFont : font });
    };
    const drawRow = (label, value, y) => {
        page.drawRectangle({ x: 40, y: y, width: 515, height: 20, borderWidth: 1, borderColor: rgb(0.8,0.8,0.8) });
        page.drawLine({ start: { x: 200, y: y }, end: { x: 200, y: y+20 }, thickness: 1, color: rgb(0.8,0.8,0.8) });
        drawText(label, 45, y + 6, 9, true);
        drawText(value, 205, y + 6, 9, false);
    };

    let y = 841.89 - 50;
    drawText("Candidate Details (Cover Page)", 40, y, 14, true);
    y -= 20;
    
    const headerData = [
        ["Name", (formData.firstName + ' ' + formData.lastName).toUpperCase()],
        ["Contact Number", formData.mobile],
        ["Email ID", formData.email],
        ["LinkedIn Profile", formData.linkedInLink],
        ["Current Location", formData.candidateCity || formData.currentLocation],
        ["Former TCS EMP/ BA", formData.tcsFormerEmployee === 'Yes' || formData.tcsFormerBusinessAssociate === 'Yes' ? 'Yes' : 'No'],
        ["EMP ID (Former TCS)", formData.formerTcsEmployeeId]
    ];

    headerData.forEach(([label, val]) => { y -= 20; drawRow(label, val, y); });

    y -= 40; drawText("Interview Availability", 40, y, 12, true); y -= 30;
    page.drawRectangle({ x: 40, y: y, width: 515, height: 20, color: rgb(0.95,0.95,0.95) });
    drawText("Date", 45, y+6, 9, true); drawText("Time Slot", 205, y+6, 9, true);
    
    // FORMATTED DATE FOR PDF: MM-DD-YYYY
    formData.interviewSlots.forEach(slot => { y -= 20; drawRow(formatToMMDDYYYY(slot.date), slot.timeSlot, y); });

    y -= 40; drawText("Skill Matrix", 40, y, 12, true); y -= 30;
    page.drawRectangle({ x: 40, y: y, width: 515, height: 20, color: rgb(0.95,0.95,0.95) });
    drawText("Skill Name", 45, y+6, 9, true); drawText("Years of Experience", 205, y+6, 9, true);
    formData.skillMatrix.forEach(item => { y -= 20; drawRow(item.skill, item.years, y); });

    y -= 40; drawText("Supplier Screening Comments", 40, y, 12, true); y -= 10;
    y -= 60;
    page.drawRectangle({ x: 40, y: y, width: 515, height: 60, borderWidth: 1, borderColor: rgb(0.8,0.8,0.8) });
    const comments = sanitizeForPDF(formData.supplierComments) || 'N/A';
    page.drawText(comments.length > 300 ? comments.substring(0, 300) + '...' : comments, { x: 45, y: y + 45, size: 9, font: font, maxWidth: 505, lineHeight: 12 });

    return await pdfDoc.save();
  };

  const mergeCoverAndResume = async (coverBytes, originalFile) => {
    try {
        const originalArrayBuffer = await originalFile.arrayBuffer();
        const pdfDoc = await PDFDocument.create();
        const coverPdf = await PDFDocument.load(coverBytes);
        const [coverPage] = await pdfDoc.copyPages(coverPdf, [0]);
        pdfDoc.addPage(coverPage);
        if (originalFile.type === 'application/pdf') {
            const originalPdf = await PDFDocument.load(originalArrayBuffer);
            const originalPages = await pdfDoc.copyPages(originalPdf, originalPdf.getPageIndices());
            originalPages.forEach((page) => pdfDoc.addPage(page));
        }
        return await pdfDoc.save();
    } catch (err) { console.error(err); return coverBytes; }
  };

  // --- REWRITTEN PDF GENERATION TO MATCH LOOK & FEEL ---
  const generateAttestationPDF = async () => {
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([595.28, 841.89]); 
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const drawText = (text, x, y, size = 10, isBold = false, color = rgb(0,0,0)) => {
        page.drawText(sanitizeForPDF(text), { x, y, size, font: isBold ? boldFont : font, color });
    };
    const drawLine = (x1, y1, x2, y2) => {
        page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness: 1, color: rgb(0, 0, 0) });
    };

    let y = 841.89 - 50;

    // Header Logo & Title
    drawText("pontoon", 40, y, 28, true);
    drawText("TCS MSP Program", 450, y, 10, true);
    y -= 15;
    drawText("Candidate Submission Attestation", 400, y, 10, true);
    y -= 12;
    drawText("Required Document", 490, y, 9, false, rgb(0.4, 0.4, 0.4));

    y -= 30;
    drawText("Overview:", 40, y, 9, true); // Bold label
    drawText(" To ensure seamless candidate consideration, on-boarding, and adherence to compliance policies, the", 90, y, 9);
    y -= 12;
    drawText("MSP requires completion of this attestation at time of candidate submission. The supplier is confirming all", 40, y, 9);
    y -= 12;
    drawText("information is accurate and agrees to obligations set forth below.", 40, y, 9);

    y -= 30;
    drawText("Required Information (complete all fields):", 40, y, 11, true);

    // --- REPLICATING THE IMAGE 2 LAYOUT EXACTLY ---
    y -= 25;
    
    // Line 1: Supplier Name (Label + Underlined Value)
    drawText("Submitting Supplier Name:", 60, y, 9);
    drawText("TENEO ADVISOROPEDIA PRIVATE LIMITED", 200, y, 9, false, rgb(0, 0.2, 0.8));
    drawLine(200, y-2, 550, y-2); // Line matches text width
    y -= 25;

    // Line 2: Employed By
    drawText("Candidate is Employed by (Registered Company Name):", 60, y, 9);
    drawText("TENEO ADVISOROPEDIA PRIVATE LIMITED", 320, y, 9, false, rgb(0, 0.2, 0.8));
    drawLine(320, y-2, 550, y-2);
    y -= 25;

    // Line 3: Candidate Name
    drawText("Candidate's Full Legal Name:", 60, y, 9);
    drawText(`${formData.firstName} ${formData.lastName}`.toUpperCase(), 200, y, 9, false, rgb(0, 0.2, 0.8));
    drawLine(200, y-2, 400, y-2);
    y -= 25;

    // Line 4: Beeline ID
    drawText("Beeline Requisition ID:", 60, y, 9);
    drawText(formData.beelineId, 170, y, 9, false, rgb(0, 0.2, 0.8));
    drawLine(170, y-2, 280, y-2);
    y -= 25;

    // Line 5: Date
    drawText("Submission Date (MM-DD-YYYY):", 60, y, 9);
    // FORMATTED DATE FOR PDF: MM-DD-YYYY
    drawText(formatToMMDDYYYY(formData.submissionDate), 220, y, 9, false, rgb(0, 0.2, 0.8));
    drawLine(220, y-2, 350, y-2);

    y -= 30;
    drawText("I attest to the following:", 40, y, 10, true);
    y -= 20;

    // --- BULLET POINTS ---
    const drawBullet = (text) => {
        page.drawText("•", { x: 50, y: y, size: 12, font: boldFont });
        page.drawText(sanitizeForPDF(text), { x: 65, y: y, size: 9, font: font, maxWidth: 480, lineHeight: 12 });
        y -= 20; 
    };

    drawBullet("Candidate's resume has been verified for accuracy with explanation of any employment gaps.");
    
    // Interview Date Line
    drawBullet("Your company conducted a formal interview with Candidate on this date:");
    // FORMATTED DATE FOR PDF: MM-DD-YYYY
    drawText(formData.formalInterviewConducted === 'Yes' ? formatToMMDDYYYY(formData.interviewDate) : "N/A", 370, y+20, 9, false, rgb(0,0.2,0.8));
    drawLine(370, y+18, 450, y+18);

    drawBullet("Is Candidate a former TCS Employee or previously worked as a TCS contractor (Y/N):");
    drawText(formData.tcsFormerEmployee === 'Yes' ? "YES" : "NO", 420, y+20, 9, false, rgb(0,0.2,0.8));
    drawLine(420, y+18, 480, y+18);

    drawBullet("Candidate meets all required qualifications and experience detailed on the Beeline Requisition");
    drawBullet("Candidate has been informed of the expected start date, location, and duration of assignment.");
    
    y -= 5;
    drawBullet("Candidate identity has been verified and is legally authorized to work in the country for the duration of the assignment. Authenticity of work authorization documentation has been confirmed.");
    y -= 10; 
    
    // Work Auth
    drawText("o   Work authorization type:", 80, y, 9);
    drawText(formData.workAuthType, 200, y, 9, false, rgb(0,0.2,0.8));
    drawLine(200, y-2, 350, y-2); 
    y -= 25;

    drawBullet("VISA Authorization:");
    y += 5;

    drawText("o   Does Candidate require VISA sponsorship now or in the future (Yes/No):", 80, y, 9);
    drawText(formData.visaSponsorship, 420, y, 9, false, rgb(0,0.2,0.8));
    drawLine(420, y-2, 460, y-2); 
    y -= 20;

    drawText("o   If Yes, Visa Type:", 80, y, 9);
    drawText(formData.visaSponsorship === 'Yes' ? formData.visaType : "NA", 165, y, 9, false, rgb(0,0.2,0.8));
    drawLine(165, y-2, 250, y-2);

    drawText("Expiration Date:", 300, y, 9);
    // FORMATTED DATE FOR PDF: MM-DD-YYYY
    drawText(formData.visaSponsorship === 'Yes' ? formatToMMDDYYYY(formData.visaExpiry) : "NA", 380, y, 9, false, rgb(0,0.2,0.8));
    drawLine(380, y-2, 460, y-2);
    y -= 20;

    drawText("o   If Selected, will the Candidate require a Visa Sponsor transfer (Yes/No):", 80, y, 9);
    drawText(formData.visaSponsorTransfer, 420, y, 9, false, rgb(0,0.2,0.8));
    drawLine(420, y-2, 460, y-2);
    y -= 25;

    drawBullet("Background screening will be initiated within 24 hours of Candidate's selection");
    drawBullet("I have retained documentation confirming all elements of this attestation are factual and can produce such evidence within 48 hours of Pontoon's request.");
    y -= 15;

    // --- BOTTOM DISCLAIMER ---
    drawText("Supplier Attestation:", 40, y, 9, true); 
    drawText(" I am an authorized supplier representative attesting all information provided is true and", 135, y, 9);
    y -= 12;
    drawText("correct. It is agreed that any misrepresentation or failures to fulfil the above obligations will be deemed breach of", 40, y, 9);
    y -= 12;
    drawText("supplier's obligations, which could result in punitive actions against the supplier.", 40, y, 9);

    // --- SIGNATURE BLOCK (RE-ALIGNED TO MATCH IMAGE 3) ---
    y -= 40;
    const sigY = y;
    const col2X = 320; // X position for right column (Date/Signature)

    // Row 1: Name & Date
    drawText("Name:", 60, sigY, 9);
    drawText("Shweta Srivastava", 100, sigY, 9, false, rgb(0,0.2,0.8));
    drawLine(100, sigY - 2, 280, sigY - 2);

    drawText("Date:", col2X, sigY, 9);
    // FORMATTED DATE FOR PDF: MM-DD-YYYY
    drawText(formatToMMDDYYYY(formData.signerDate), col2X + 30, sigY, 9, false, rgb(0,0.2,0.8));
    drawLine(col2X + 30, sigY - 2, 530, sigY - 2);

    // Row 2: Title (Left only)
    y -= 25;
    drawText("Title:", 60, y, 9);
    drawText("Account Manager", 100, y, 9, false, rgb(0,0.2,0.8));
    drawLine(100, y - 2, 280, y - 2);

    // Row 3: Email & Signature
    y -= 25;
    drawText("Email:", 60, y, 9);
    drawText("Shweta.Srivastava@talentoza.com", 100, y, 9, false, rgb(0,0.2,0.8));
    drawLine(100, y - 2, 280, y - 2);

    drawText("Signature:", col2X, y, 9);
    // Draw Signature in Larger, Bold Font to mimic handwriting style
    drawText("Shweta Srivastava", col2X + 50, y + 2, 14, true, rgb(0,0.2,0.8)); 
    drawLine(col2X + 50, y - 2, 530, y - 2);

    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
  };

  const handlePreviewAttestation = async () => {
    const pdfBytes = await generateAttestationPDF();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    window.open(URL.createObjectURL(blob), '_blank');
  };

  const handlePreviewMergedPDF = async () => {
    if (!resumeFile && !initialData) { alert("Please upload a resume in Step 1 first."); return; }
    const coverBytes = await generateCoverPagePDF();
    const mergedBytes = resumeFile ? await mergeCoverAndResume(coverBytes, resumeFile) : coverBytes;
    const blob = new Blob([mergedBytes], { type: 'application/pdf' });
    window.open(URL.createObjectURL(blob), '_blank');
  };

    const nextStep = () => {
    if (currentStep === 1 && formData.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.panNumber)) {
      alert("Invalid PAN format. Expected format: ABCDE1234F");
      return;
    }
    setCurrentStep(prev => prev + 1);
    window.scrollTo(0, 0);
  };
  const prevStep = () => { setCurrentStep(prev => prev - 1); window.scrollTo(0, 0); };
  
  const handleFinalSubmit = async () => {
      try {
        const attestationBytes = await generateAttestationPDF();
        const attestationBlob = new Blob([attestationBytes], { type: 'application/pdf' });
        
        const coverBytes = await generateCoverPagePDF();
        let modifiedResumeBlob;
        if (resumeFile && resumeFile.type === 'application/pdf') {
            const mergedBytes = await mergeCoverAndResume(coverBytes, resumeFile);
            modifiedResumeBlob = new Blob([mergedBytes], { type: 'application/pdf' });
        } else {
            modifiedResumeBlob = new Blob([coverBytes], { type: 'application/pdf' });
        }

        const submissionData = new FormData();
        const isUpdate = !!initialData;
        const detectedPartnerId = partner?.id || partner?._id || partner?.vendorId || partner?.recruiterId || undefined;
        const isVendor = partner?.vendorName || partner?.role === 'vendor';
        const isRecruiter = partner?.recruiterName || partner?.username || partner?.role === 'recruiter';

        // Smart Payload: Keeps existing submittedBy/vendorId if Admin is just editing
        const finalPayload = { 
            ...formData, 
            jobId: job?._id || job?.id || initialData?.jobId,
            vendorId: isUpdate ? initialData.vendorId : (isVendor ? detectedPartnerId : undefined), 
            recruiterId: isUpdate ? initialData.recruiterId : (isRecruiter ? detectedPartnerId : undefined),
            submittedBy: isUpdate ? initialData.submittedBy : (partner?.vendorName || partner?.recruiterName || partner?.username || 'Admin')
        };
        
        submissionData.append('data', JSON.stringify(finalPayload));
        if (resumeFile) submissionData.append('originalResume', resumeFile);
        submissionData.append('modifiedResume', modifiedResumeBlob, `Modified_${formData.firstName}_Resume.pdf`);
        submissionData.append('attestationPdf', attestationBlob, 'Pontoon_Attestation.pdf');

        // ROUTE SWITCHER: PUT for Edit, POST for New
        const endpoint = isUpdate 
            ? `${process.env.REACT_APP_API_BASE_URL}/api/beeline/update/${initialData.id}` 
            : `${process.env.REACT_APP_API_BASE_URL}/api/beeline/submit`;
            
        const method = isUpdate ? axios.put : axios.post;

        const response = await method(endpoint, submissionData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success) {
            alert(isUpdate ? "Submission Updated Successfully!" : "Submission Successful!");
            onBack(); 
        }

      } catch (error) {
          console.error("Submission Error:", error);
          alert("Submission Failed: " + (error.response?.data?.error || error.message));
      }
  };

  const renderStep1 = () => (
    <div className="bs-card">
        <div className="bs-section-header"><h3>1. Identity & Personal Details</h3></div>
        <div className="bs-grid-2">
            <div className="bs-input-group"><label>1. Request ID</label><input name="requestId" value={formData.requestId} onChange={handleChange} placeholder="Enter Request ID" /></div>
            <div className="bs-input-group"><label>2. Beeline ID</label><input value={formData.beelineId} readOnly className="read-only" /></div>
            <div className="bs-input-group"><label>3. First Name <span className="req">*</span></label><input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="CAPITAL LETTERS" style={{textTransform:'uppercase'}}/></div>
            <div className="bs-input-group"><label>4. Last Name <span className="req">*</span></label><input name="lastName" value={formData.lastName} onChange={handleChange} style={{textTransform:'uppercase'}}/></div>
            <div className="bs-input-group"><label>5. Email (Parsed)</label><input name="email" value={formData.email} onChange={handleChange} type="email" /></div>
            <div className="bs-input-group"><label>6. DOB</label><input name="dob" type="date" value={formData.dob} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>7. Initial Personal ID (Auto)</label><input name="initialPersonalId" value={formData.initialPersonalId} readOnly style={{backgroundColor: '#f1f5f9', cursor: 'not-allowed'}} /></div>
            <div className="bs-input-group"><label>8. Full Personal ID (Auto)</label><input name="fullPersonalId" value={formData.fullPersonalId} readOnly style={{backgroundColor: '#f1f5f9', cursor: 'not-allowed'}} /></div>
            <div className="bs-input-group"><label>9. PAN Number</label><input name="panNumber" value={formData.panNumber} onChange={handlePanChange} maxLength={10} style={{textTransform:'uppercase'}} /></div>
            <div className="bs-input-group"><label>10. Former TCS Emp?</label><select name="tcsFormerEmployee" value={formData.tcsFormerEmployee} onChange={handleChange}><option value="No">No</option><option value="Yes">Yes</option></select></div>
            <div className="bs-input-group"><label>11. Former TCS BA?</label><select name="tcsFormerBusinessAssociate" value={formData.tcsFormerBusinessAssociate} onChange={handleChange}><option value="No">No</option><option value="Yes">Yes</option></select></div>
            <div className="bs-input-group"><label>12. Gender</label><select name="gender" value={formData.gender} onChange={handleChange}><option value="">Select</option><option value="Male">Male</option><option value="Female">Female</option></select></div>
            <div className="bs-input-group"><label>13. Nationality</label><input name="nationality" value={formData.nationality} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>14. Availability</label><select name="availability" value={formData.availability} onChange={handleChange}><option value="Yes">Yes</option><option value="No">No</option></select></div>
            <div className="bs-input-group"><label>15. Date Available</label><input name="dateAvailable" type="date" value={formData.dateAvailable} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>16. LinkedIn</label><input name="linkedInLink" value={formData.linkedInLink} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>17. Mobile</label><input name="mobile" value={formData.mobile} onChange={handleChange} /></div>
        </div>
        <div className="bs-divider"></div>
        <div className="bs-section-header"><h3>2. Additional Information</h3></div>
        <div className="bs-grid-2">
            <div className="bs-input-group"><label>18. Total IT Exp</label><input name="totalITExperience" value={formData.totalITExperience} onChange={handleWholeNumberChange} type="text" inputMode="numeric" /></div>
            <div className="bs-input-group"><label>19. Req Skills Exp</label><input name="experienceRequiredSkills" value={formData.experienceRequiredSkills} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>20. Highest Degree</label><input name="highestDegree" value={formData.highestDegree} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>21. Specialization</label><input name="specialization" value={formData.specialization} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>22. Edu Start Date</label><input name="educationStartDate" type="date" value={formData.educationStartDate} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>23. Edu End Date</label><input name="educationEndDate" type="date" value={formData.educationEndDate} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>24. Recent Company</label><input name="recentCompany" value={formData.recentCompany} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>25. Current Org Start Date</label><input name="currentOrgStartDate" type="date" value={formData.currentOrgStartDate} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>26. Current Org End Date</label><input name="currentOrgEndDate" type="date" value={formData.currentOrgEndDate} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>27. Company Address</label><input name="recentCompanyAddress" value={formData.recentCompanyAddress} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>28. Candidate Address</label><input name="candidateAddress" value={formData.candidateAddress} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>29. Candidate City</label><input name="candidateCity" value={formData.candidateCity} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>30. Candidate State</label><input name="candidateState" value={formData.candidateState} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>31. Candidate Pincode</label><input name="candidatePincode" value={formData.candidatePincode} onChange={handleChange} /></div>
        </div>
        <div className="bs-divider"></div>
        <div className="bs-section-header"><h3>3. Candidate Info & Rates</h3></div>
        <div className="bs-grid-2">
            <div className="bs-input-group"><label>32. Resource Type ID</label><input name="resourceTypeId" value={formData.resourceTypeId} onChange={handleChange} /></div>
            <div className="bs-input-group"><label>33. Date Available (Auto)</label><input value={formatToMMDDYYYY(formData.dateAvailable)} readOnly className="read-only" /></div>
            <div className="bs-input-group"><label>34. Submitted Pay Rate</label><input name="submittedRate" value={formData.submittedRate} onChange={handleSubmittedRateChange} type="number" step="any" className="no-spinner" onWheel={(e) => e.target.blur()} /></div>
            <div className="bs-input-group"><label>35. Submitted Rate</label><input name="submittedPayRate" value={formData.submittedPayRate} onChange={handleChange} type="number" step="any" className="no-spinner" onWheel={(e) => e.target.blur()} /></div>
        </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="bs-card">
      <div className="bs-info-banner"><Info size={18} /><strong>Resume Cover Page</strong></div>
      <div className="bs-table-container">
        <div className="bs-table-title">Candidate Details (Cover Header)</div>
        <table className="bs-main-table">
            <tbody>
                <tr>
                    <td className="bs-label-cell">Name (Capital Letters)</td>
                    <td className="bs-input-cell"><div style={{display:'flex', gap:'5px'}}><input name="firstName" value={formData.firstName} onChange={handleChange} className="bs-table-input" style={{textTransform:'uppercase'}} /><input name="lastName" value={formData.lastName} onChange={handleChange} className="bs-table-input" style={{textTransform:'uppercase'}} /></div></td>
                </tr>
                <tr><td className="bs-label-cell">Contact Number</td><td className="bs-input-cell"><input name="mobile" value={formData.mobile} onChange={handleChange} className="bs-table-input" /></td></tr>
                <tr><td className="bs-label-cell">Email ID</td><td className="bs-input-cell"><input name="email" value={formData.email} onChange={handleChange} className="bs-table-input" /></td></tr>
                <tr><td className="bs-label-cell">LinkedIn profile link</td><td className="bs-input-cell"><input name="linkedInLink" value={formData.linkedInLink} onChange={handleChange} className="bs-table-input" /></td></tr>
                <tr><td className="bs-label-cell">Current Location</td><td className="bs-input-cell"><input name="candidateCity" value={formData.candidateCity} onChange={handleChange} className="bs-table-input" /></td></tr>
                <tr><td className="bs-label-cell">Former TCS EMP/ BA</td><td className="bs-input-cell"><select name="tcsFormerEmployee" value={formData.tcsFormerEmployee} onChange={handleTcsStatusChange} className="bs-table-select"><option value="No">No</option><option value="Yes">Yes</option></select></td></tr>
                <tr><td className="bs-label-cell">EMP ID (Former TCS)</td><td className="bs-input-cell"><input name="formerTcsEmployeeId" value={formData.formerTcsEmployeeId} onChange={handleChange} className="bs-table-input" readOnly={formData.tcsFormerEmployee === 'No'} style={{ backgroundColor: formData.tcsFormerEmployee === 'No' ? '#f1f5f9' : 'white', cursor: formData.tcsFormerEmployee === 'No' ? 'not-allowed' : 'text' }} /></td></tr>
            </tbody>
        </table>
      </div>
      <div className="bs-section-header" style={{marginTop:'30px'}}><h3>Interview Availability</h3></div>
      <div className="bs-dynamic-wrapper">
        <div className="bs-dynamic-header"><span>Date</span><span>Time Slot</span><span>Action</span></div>
        {formData.interviewSlots.map((slot, index) => (
            <div key={index} className="bs-dynamic-row">
                <input type="date" value={slot.date} onChange={(e) => handleSlotChange(index, 'date', e.target.value)} />
                <input type="text" placeholder="e.g. 2:00 PM - 4:00 PM" value={slot.timeSlot} onChange={(e) => handleSlotChange(index, 'timeSlot', e.target.value)} />
                {index > 0 && <button onClick={() => { const newSlots = formData.interviewSlots.filter((_, i) => i !== index); setFormData(prev => ({...prev, interviewSlots: newSlots})); }} className="bs-icon-btn"><Trash2 size={16}/></button>}
            </div>
        ))}
        <button className="bs-add-row-btn" onClick={handleAddSlot}><Plus size={14}/> Add Slot</button>
      </div>
      <div className="bs-section-header" style={{marginTop:'30px'}}><h3>Skill Matrix</h3></div>
      <div className="bs-dynamic-wrapper">
        <div className="bs-dynamic-header"><span>Skill Name</span><span>Years of Experience</span><span>Action</span></div>
        {formData.skillMatrix.map((item, index) => (
            <div key={index} className="bs-dynamic-row">
                <input type="text" placeholder="e.g. Java" value={item.skill} onChange={(e) => handleSkillChange(index, 'skill', e.target.value)} />
                <input type="number" placeholder="Years" value={item.years} onChange={(e) => handleSkillChange(index, 'years', e.target.value)} />
                {index > 0 && <button onClick={() => { const newSkills = formData.skillMatrix.filter((_, i) => i !== index); setFormData(prev => ({...prev, skillMatrix: newSkills})); }} className="bs-icon-btn"><Trash2 size={16}/></button>}
            </div>
        ))}
        <button className="bs-add-row-btn" onClick={handleAddSkill}><Plus size={14}/> Add Skill</button>
      </div>
      <div className="bs-section-header" style={{marginTop:'30px'}}><h3>Supplier Screening Comments</h3></div>
      <div style={{padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc'}}>
          <textarea name="supplierComments" value={formData.supplierComments} onChange={handleChange} placeholder="Enter any additional screening comments here..." style={{ width: '100%', minHeight: '80px', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem', fontFamily: 'inherit' }} />
      </div>
      <div style={{ marginTop: '20px', textAlign: 'center' }}><button className="bs-pdf-btn" onClick={handlePreviewMergedPDF}><Eye size={18} /> View Asserted Resume (PDF Preview)</button></div>
    </div>
  );

  // --- RENDER STEP 3 (UPDATED UI TO MATCH DOCUMENT LOOK) ---
  const renderStep3 = () => (
    <div className="bs-card" style={{ maxWidth: '800px', margin: '0 auto', padding: '40px', boxSizing: 'border-box' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '30px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>pontoon</h2>
        <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold' }}>TCS MSP Program</div>
            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>Candidate Submission Attestation</div>
            <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>Required Document</div>
        </div>
      </div>

      <div className="doc-section-header">Required Information (complete all fields):</div>

      {/* --- FORM FIELDS (LOOK LIKE DOCUMENT LINES) --- */}
      
      <div className="doc-line-row">
          <span className="doc-label">Submitting Supplier Name:</span>
          <input className="doc-input-underline" value={formData.supplierName} readOnly />
      </div>

      <div className="doc-line-row">
          <span className="doc-label">Candidate is Employed by (Registered Company Name):</span>
          <input className="doc-input-underline" value={formData.employedBy} readOnly />
      </div>

      <div className="doc-line-row">
          <span className="doc-label">Candidate's Full Legal Name:</span>
          <input 
            className="doc-input-underline" 
            value={`${formData.firstName} ${formData.lastName}`.toUpperCase()} 
            readOnly 
          />
      </div>

      <div className="doc-row-split" style={{ display: 'flex', gap: '30px' }}>
          <div className="doc-line-row" style={{ flex: 1 }}>
              <span className="doc-label">Beeline Requisition ID:</span>
              <input className="doc-input-underline" value={formData.beelineId} readOnly />
          </div>
          <div className="doc-line-row" style={{ flex: 1 }}>
              <span className="doc-label">Submission Date:</span>
              {/* FORMATTED DISPLAY DATE */}
              <input className="doc-input-underline" value={formatToMMDDYYYY(formData.submissionDate)} readOnly />
          </div>
      </div>

      {/* --- ATTESTATION QUESTIONS --- */}
      <div className="doc-section-header" style={{ marginTop: '30px' }}>I attest to the following:</div>
      
      <div className="doc-attest-block">
          <div className="doc-question-row">
              <span>Resume verified for accuracy?</span>
              <select className="bs-table-select" name="resumeVerified" value={formData.resumeVerified} onChange={handleChange} style={{width:'80px'}}>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
              </select>
          </div>

          <div className="doc-question-row">
              <span>Formal interview conducted?</span>
              <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
                  {formData.formalInterviewConducted === 'Yes' && (
                      <input type="date" name="interviewDate" value={formData.interviewDate} onChange={handleChange} style={{border:'1px solid #ccc', padding:'2px'}} />
                  )}
                  <select className="bs-table-select" name="formalInterviewConducted" value={formData.formalInterviewConducted} onChange={handleChange} style={{width:'80px'}}>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                  </select>
              </div>
          </div>

          <div className="doc-question-row">
              <span>Is Candidate a former TCS Employee/Contractor?</span>
              <select className="bs-table-select" name="tcsFormerEmployee" value={formData.tcsFormerEmployee} onChange={handleChange} style={{width:'80px'}}>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
              </select>
          </div>

          <div className="doc-line-row" style={{ marginTop: '15px' }}>
              <span className="doc-label">Work authorization type:</span>
              <input 
                className="doc-input-underline" 
                name="workAuthType" 
                value={formData.workAuthType} 
                onChange={handleChange} 
                placeholder="e.g. INDIAN"
              />
          </div>

          <div style={{ fontWeight: 'bold', marginTop: '15px', textDecoration: 'underline' }}>VISA Authorization:</div>
          
          <div className="doc-question-row" style={{border: 'none'}}>
              <span>Does Candidate require VISA sponsorship now or in the future?</span>
              <select className="bs-table-select" name="visaSponsorship" value={formData.visaSponsorship} onChange={handleChange} style={{width:'80px'}}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
              </select>
          </div>

          {formData.visaSponsorship === 'Yes' && (
             <div className="doc-row-split" style={{ display: 'flex', gap: '20px', paddingLeft: '20px', marginBottom: '10px' }}>
                 <div className="doc-line-row" style={{ flex: 1 }}>
                     <span className="doc-label">Visa Type:</span>
                     <input className="doc-input-underline" name="visaType" value={formData.visaType} onChange={handleChange} />
                 </div>
                 <div className="doc-line-row" style={{ flex: 1 }}>
                     <span className="doc-label">Exp Date:</span>
                     <input type="date" className="doc-input-underline" name="visaExpiry" value={formData.visaExpiry} onChange={handleChange} />
                 </div>
             </div>
          )}

          <div className="doc-question-row">
              <span>If Selected, will the Candidate require a Visa Sponsor transfer?</span>
              <select className="bs-table-select" name="visaSponsorTransfer" value={formData.visaSponsorTransfer} onChange={handleChange} style={{width:'80px'}}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
              </select>
          </div>
      </div>

      {/* --- SIGNATURE BLOCK --- */}
      <div className="doc-sig-grid">
          <div>
              <div className="doc-line-row">
                  <span className="doc-label" style={{width: '60px'}}>Name:</span>
                  <input className="doc-input-underline" value={formData.signerName} readOnly />
              </div>
              <div className="doc-line-row">
                  <span className="doc-label" style={{width: '60px'}}>Title:</span>
                  <input className="doc-input-underline" value={formData.signerTitle} readOnly />
              </div>
              <div className="doc-line-row">
                  <span className="doc-label" style={{width: '60px'}}>Email:</span>
                  <input className="doc-input-underline" value={formData.signerEmail} readOnly style={{fontSize:'12px'}} />
              </div>
          </div>

          <div>
              <div className="doc-line-row">
                  <span className="doc-label" style={{width: '80px'}}>Date:</span>
                  {/* FORMATTED DISPLAY DATE */}
                  <input className="doc-input-underline" value={formatToMMDDYYYY(formData.signerDate)} readOnly />
              </div>
              <div className="doc-line-row" style={{ marginTop: '45px' }}>
                  <span className="doc-label" style={{width: '80px'}}>Signature:</span>
                  <div className="doc-input-underline cursive-sig">
                      {formData.digitalSignature}
                  </div>
              </div>
          </div>
      </div>

      <div style={{ marginTop: '40px', textAlign: 'center' }}>
          <button className="bs-pdf-btn" onClick={handlePreviewAttestation}>
            <Download size={18} /> Preview/Download Generated Attestation PDF
          </button>
      </div>
    </div>
  );

  return (
    <div className="beeline-form-wrapper">
      <div className="bf-header">
        <button onClick={onBack} className="bf-back-btn"><ArrowLeft size={18}/> Cancel</button>
        <div className="bf-title-group"><h2>Beeline Submission Wizard</h2></div>
        <div className="bf-progress"><div className={`bf-step ${currentStep >= 1 ? 'active' : ''}`}>1. Data</div><div className="bf-line"></div><div className={`bf-step ${currentStep >= 2 ? 'active' : ''}`}>2. Cover</div><div className="bf-line"></div><div className={`bf-step ${currentStep >= 3 ? 'active' : ''}`}>3. Attest</div></div>
      </div>
      <div className="bf-content">
        {currentStep === 1 && (
            <>
                {!initialData && (
                    <div className="bf-upload-box">
                        {isUploading ? <Loader2 className="spin" /> : <Upload size={24}/>}
                        <div style={{flex:1}}>
                            <h4 style={{margin: 0}}>Upload Resume</h4>
                            <span style={{fontSize: '12px', color: '#ef4444', fontWeight: '500'}}>*Only PDFs accepted</span>
                        </div>
                        <input type="file" accept=".pdf,application/pdf" onChange={handleFileUpload}/>
                    </div>
                )}
                {renderStep1()}
            </>
        )}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>
      <div className="bf-footer">
        {currentStep > 1 && <button className="bf-btn secondary" onClick={prevStep}>Previous</button>}
        <div style={{flex:1}}></div>
        {currentStep < 3 ? <button className="bf-btn primary" onClick={nextStep}>Next Step</button> : <button className="bf-btn success" onClick={handleFinalSubmit} disabled={!formData.digitalSignature}>{initialData ? "Update Submission" : "Final Submit"}</button>}
      </div>
    </div>
  );
}